import { frameAtTime, type SimulationResult } from '@rcs/physics';
import {
  CAMERA_PRESETS,
  createViewport,
  type CameraPreset,
  type ControlPointMarker,
  type DragPlane,
  carPitch,
  type RenderQuality,
  type TrackSections,
  type EnvironmentLook,
  type VehicleModelSpec,
  type Viewport,
} from '@rcs/renderer';
import type { Vec3 } from '@rcs/domain';
import type { SegmentType } from '@rcs/data-schema';
import { ToolButton } from '@rcs/ui';
import { useEffect, useRef, useState } from 'react';
import { keyForView } from '../camera/shortcuts';
import { carPoses } from '../course/cars';
import { VIEW_KINDS, viewToCamera, type ViewKind } from '../camera/view';
import { messages } from '../i18n/ja';
import { useMovableWindow } from '../layout/movable';
import type { PlaybackController } from '../playback/controller';
import { isPedaling, segmentKindAt } from '../ride/pedaling';
import type { ComputeSuccess } from '../worker/protocol';

/** 3D ビューの初期化状態。E2E テストでは `data-renderer` 属性で確認する。 */
type ViewportStatus = 'initializing' | 'webgl2' | 'unsupported';

/**
 * Three.js のビューポートを React からマウントする。
 * 描画ループは `@rcs/renderer` 側で動き、React の再描画とは独立している（ADR-0004）。
 */
export function Viewport3D({
  ride,
  result,
  fitKey,
  sections,
  segmentKinds,
  vehicleModel,
  environmentLook,
  playback,
  quality = 'medium',
  view = 'overview',
  onViewChange,
  onFps,
  editing = false,
  dragPlane = 'free',
  controlPoints = [],
  onControlPointSelect,
  onControlPointMoveStart,
  onControlPointMove,
  onControlPointMoveEnd,
  onControlPointLongPress,
}: {
  readonly ride?: ComputeSuccess | undefined;
  /**
   * 車両を動かす走行の結果（走行中の操作を反映したもの）。省略は `ride.result`。
   * 変わってもコースは作り直さず、再生位置のまま車両の動きだけを差し替える
   */
  readonly result?: SimulationResult | undefined;
  /** 表示するコースの識別子。変わったら（コースを切り替えたら）、全体俯瞰でカメラをコース全体に合わせ直す */
  readonly fitKey?: string | undefined;
  /** 車両の見た目（3D のモデル・乗客。編成の車両数・寸法）。省略は標準のコースター 1 両 */
  readonly vehicleModel?: VehicleModelSpec | undefined;
  /** 背景（空・地面・光・霧）の見た目。省略は既定（昼・格子の地面） */
  readonly environmentLook?: EnvironmentLook | undefined;
  /** 区間ごとの表示（色・設備：リフトのチェーン・ブレーキのフィンなど） */
  readonly sections?: TrackSections | undefined;
  /** 制御点ごとの区間種別（自転車の人が、巻き上げなどでこがないようにするために使う） */
  readonly segmentKinds?: readonly SegmentType[] | undefined;
  readonly playback?: PlaybackController | undefined;
  /** 描画品質。変えるとビューポートを作り直す（アンチエイリアスの切り替えのため） */
  readonly quality?: RenderQuality | undefined;
  /** 選んでいる視点（全体俯瞰・追従） */
  readonly view?: ViewKind | undefined;
  readonly onViewChange?: ((view: ViewKind) => void) | undefined;
  /** 描画 FPS（0.5 秒ごとに通知。デバッグ表示用） */
  readonly onFps?: ((fps: number) => void) | undefined;
  /** 編集中か（制御点の目印を表示し、選択・移動できる） */
  readonly editing?: boolean | undefined;
  /** 制御点をドラッグするときの移動面（移動の制約に合わせる） */
  readonly dragPlane?: DragPlane | undefined;
  readonly controlPoints?: readonly ControlPointMarker[] | undefined;
  readonly onControlPointSelect?: ((id: string | undefined) => void) | undefined;
  readonly onControlPointMoveStart?: ((id: string) => void) | undefined;
  readonly onControlPointMove?: ((id: string, position: Vec3) => void) | undefined;
  readonly onControlPointMoveEnd?: ((id: string) => void) | undefined;
  /** 制御点を動かさずに長押しした（メニューを出す）。位置は画面の座標 */
  readonly onControlPointLongPress?: ((id: string, x: number, y: number) => void) | undefined;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    attach: viewsAttach,
    style: viewsStyle,
    handle: viewsHandle,
  } = useMovableWindow<HTMLDivElement>('views', messages.windows.names.views);
  const [status, setStatus] = useState<ViewportStatus>('initializing');
  const [contextLost, setContextLost] = useState(false);
  // ビューポートを作り直すたびに増やし、車両・コースの表示をやり直すきっかけにする
  const [generation, setGeneration] = useState(0);
  const viewportRef = useRef<Viewport | undefined>(undefined);
  // カメラを最後に合わせたコースと、現在の視点（描画の効果から参照する）
  const fittedKeyRef = useRef<string | undefined>(undefined);
  const viewRef = useRef<ViewKind>(view);
  useEffect(() => {
    viewRef.current = view;
  }, [view]);
  // 車両を動かす結果（描画ループから参照する。変わったら、次のフレームで車両を置き直す）
  const resultRef = useRef<SimulationResult | undefined>(result);
  const resultVersionRef = useRef(0);
  const segmentKindsRef = useRef(segmentKinds);
  useEffect(() => {
    resultRef.current = result;
    resultVersionRef.current += 1;
  }, [result]);
  useEffect(() => {
    segmentKindsRef.current = segmentKinds;
  }, [segmentKinds]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let viewport: Viewport | undefined;
    let nextStatus: ViewportStatus;
    try {
      viewport = createViewport(container, {
        quality,
        onContextLostChange: setContextLost,
        // E2E テストで視点の変化を確認するため、状態を属性に出す
        onCameraChange: (state) => {
          container.dataset.cameraDistance = state.distance.toFixed(2);
          container.dataset.cameraAzimuth = state.azimuth.toFixed(3);
          container.dataset.cameraPolar = state.polar.toFixed(3);
          container.dataset.cameraLookYaw = state.look.yaw.toFixed(3);
          container.dataset.cameraLookPitch = state.look.pitch.toFixed(3);
          container.dataset.cameraTarget = state.target.map((v) => v.toFixed(2)).join(',');
        },
      });
      viewportRef.current = viewport;
      nextStatus = 'webgl2';
    } catch (error) {
      console.warn(error);
      nextStatus = 'unsupported';
    }
    // 初期化結果はブラウザが描画した後に反映する（エフェクト内での同期的な再描画を避ける）
    const frame = requestAnimationFrame(() => {
      setStatus(nextStatus);
      setGeneration((count) => count + 1);
      if (viewport) {
        // E2E テストで描画設定を確認するため、属性に出す
        const settings = viewport.getRenderSettings();
        container.dataset.quality = settings.quality;
        container.dataset.pixelRatio = String(settings.pixelRatio);
        container.dataset.shadows = String(settings.shadows);
        container.dataset.antialias = String(settings.antialias);
      }
    });

    return () => {
      cancelAnimationFrame(frame);
      viewportRef.current = undefined;
      viewport?.dispose();
    };
  }, [quality]);

  // 制御点の編集：目印の表示と、操作の通知先。通知先は最新の関数を呼ぶ（再登録しない）
  const handlersRef = useRef({
    onControlPointSelect,
    onControlPointMoveStart,
    onControlPointMove,
    onControlPointMoveEnd,
    onControlPointLongPress,
  });
  useEffect(() => {
    handlersRef.current = {
      onControlPointSelect,
      onControlPointMoveStart,
      onControlPointMove,
      onControlPointMoveEnd,
      onControlPointLongPress,
    };
  });
  useEffect(() => {
    const viewport = viewportRef.current;
    const container = containerRef.current;
    if (!viewport || !container) return undefined;
    viewport.setControlPointHandlers({
      onSelect: (id) => {
        handlersRef.current.onControlPointSelect?.(id);
      },
      onMoveStart: (id) => {
        handlersRef.current.onControlPointMoveStart?.(id);
      },
      onMove: (id, position) => {
        handlersRef.current.onControlPointMove?.(id, position);
      },
      onMoveEnd: (id) => {
        handlersRef.current.onControlPointMoveEnd?.(id);
      },
      onLongPress: (id, x, y) => {
        handlersRef.current.onControlPointLongPress?.(id, x, y);
      },
    });
    // E2E テストが、目印の画面上の位置を取れるようにする（本番の動作には使わない）
    Object.assign(container, {
      rcsControlPointScreen: (id: string) => viewport.getControlPointScreenPosition(id),
      rcsTieColors: () => viewport.getTieColors(),
      rcsDecorations: () => viewport.getDecorationCounts(),
      rcsVehicleInfo: () => viewport.getVehicleInfo(),
    });
    return () => {
      viewport.setControlPointHandlers(undefined);
    };
  }, [status, generation]);
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.setEditing(editing);
    viewport.setDragPlane(dragPlane);
    viewport.setControlPoints(controlPoints);
  }, [editing, dragPlane, controlPoints, status, generation]);

  // 視点（全体俯瞰・追従）をビューポートへ反映する
  useEffect(() => {
    const viewport = viewportRef.current;
    const container = containerRef.current;
    if (!viewport || !container) return;
    const camera = viewToCamera(view);
    viewport.setCameraMode(camera.mode, camera.options);
    container.dataset.cameraMode = camera.mode;
  }, [view, status, generation]);

  // 背景（空・地面・光・霧）を切り替える
  useEffect(() => {
    const viewport = viewportRef.current;
    const container = containerRef.current;
    if (!viewport || !container) return;
    viewport.setEnvironment(environmentLook);
    container.dataset.environmentGround = environmentLook?.ground ?? 'grid';
    container.dataset.environmentSky = environmentLook?.skyHorizon ?? '';
  }, [environmentLook, status, generation]);

  // 車両の見た目（モデル・乗客）を切り替える
  useEffect(() => {
    const viewport = viewportRef.current;
    const container = containerRef.current;
    if (!viewport || !container) return;
    viewport.setVehicleModel(vehicleModel);
    const info = viewport.getVehicleInfo();
    container.dataset.vehicleModel = info.model;
    container.dataset.vehicleCars = String(info.cars);
    container.dataset.vehicleRiders = String(info.riders);
  }, [vehicleModel, status, generation]);

  // 再生位置（PlaybackController）に合わせて、結果を補間して車両を動かす（FR-SIM-001）
  useEffect(() => {
    const viewport = viewportRef.current;
    const container = containerRef.current;
    if (!ride || !playback || !viewport || !container) return undefined;
    viewport.setTrack(ride.track, sections);
    // 別のコースに切り替えたときは、カメラを新しいコース全体に合わせる（同じコースの編集では動かさない）
    if (
      fitKey !== undefined &&
      fittedKeyRef.current !== undefined &&
      fittedKeyRef.current !== fitKey &&
      viewRef.current === 'overview'
    ) {
      viewport.setCameraPreset('oblique');
    }
    fittedKeyRef.current = fitKey;
    // コースを表示し、カメラを合わせ終えたことを、E2E テストが待てるように属性で示す
    container.dataset.trackLoaded = 'true';
    let animation = 0;
    let lastNow: number | undefined;
    let lastReport = 0;
    let lastStats = 0;
    let lastTime = -1;
    let lastResultVersion = resultVersionRef.current;
    let frames = 0;
    let fpsStart = 0;
    const tick = (now: number): void => {
      frames += 1;
      if (fpsStart === 0) fpsStart = now;
      if (now - fpsStart >= 500) {
        onFps?.(Math.round((frames * 1000) / (now - fpsStart)));
        frames = 0;
        fpsStart = now;
      }
      // タブが隠れた後などの大きな間隔は、0.1 秒に丸める
      if (lastNow !== undefined) playback.advance(Math.min((now - lastNow) / 1000, 0.1));
      lastNow = now;
      // 位置が変わったときだけ、車両を更新して再描画を要求する
      if (playback.time !== lastTime || resultVersionRef.current !== lastResultVersion) {
        lastTime = playback.time;
        lastResultVersion = resultVersionRef.current;
        const frame = frameAtTime(resultRef.current ?? ride.result, playback.time);
        // 編成の車両を、1 両ずつ軌道に沿って置く（先頭の車両の中心が、走行の位置）
        viewport.setVehicle(
          vehicleModel === undefined
            ? { position: frame.position, ...frame.orientation }
            : carPoses(ride.track, frame.s, vehicleModel.carCount, carPitch(vehicleModel)),
        );
        // 走った距離に合わせて、車輪・ペダルを動かす。巻き上げ・ブレーキなどでは、人はこがない
        const pedaling = isPedaling(
          segmentKindAt(ride.track, segmentKindsRef.current, frame.s),
          frame.speed,
        );
        viewport.setVehicleMotion({ distance: frame.s, pedaling });
        if (now - lastReport > 100 || !playback.getSnapshot().playing) {
          container.dataset.vehicleSpeed = frame.speed.toFixed(2);
          container.dataset.vehiclePedaling = String(pedaling);
          lastReport = now;
        }
      }
      // 描画の統計（テスト用）。止まっているときも、車両を作り直した後の描画を反映する
      if (now - lastStats > 100) {
        container.dataset.renderGeometries = String(viewport.getRenderStats().geometries);
        lastStats = now;
      }
      animation = requestAnimationFrame(tick);
    };
    animation = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(animation);
      viewport.setVehicle(undefined);
    };
  }, [ride, fitKey, sections, vehicleModel, playback, status, generation, onFps]);

  return (
    <section
      ref={containerRef}
      className="viewport"
      aria-label={messages.viewport.label}
      data-testid="viewport"
      data-renderer={status}
    >
      {status === 'initializing' && (
        <p className="viewport__message" role="status">
          {messages.viewport.initializing}
        </p>
      )}
      {status === 'unsupported' && (
        <div className="viewport__message" role="alert">
          <strong>{messages.viewport.unsupportedTitle}</strong>
          <p>{messages.viewport.unsupportedBody}</p>
        </div>
      )}
      {status === 'webgl2' && (
        <>
          <div ref={viewsAttach} className="camera-stack" style={viewsStyle}>
            <div className="camera-bar" role="group" aria-label={messages.camera.group}>
              {viewsHandle}
              {VIEW_KINDS.map((kind) => (
                <ToolButton
                  key={kind}
                  label={messages.camera.views[kind]}
                  pressed={view === kind}
                  aria-keyshortcuts={keyForView(kind)}
                  title={`${messages.camera.views[kind]}（${keyForView(kind)}）`}
                  data-testid={`view-${kind}`}
                  onClick={() => {
                    onViewChange?.(kind);
                  }}
                />
              ))}
            </div>
            {view === 'passenger' && (
              <p className="viewport__hint" data-testid="look-hint">
                {messages.camera.lookHint}
              </p>
            )}
            <div
              className="camera-bar camera-bar--presets"
              role="group"
              aria-label={messages.camera.presetsGroup}
            >
              {CAMERA_PRESETS.map((preset: CameraPreset) => (
                <ToolButton
                  key={preset}
                  label={messages.camera.presets[preset]}
                  disabled={view !== 'overview'}
                  data-testid={`camera-preset-${preset}`}
                  onClick={() => {
                    viewportRef.current?.setCameraPreset(preset);
                  }}
                />
              ))}
            </div>
          </div>
        </>
      )}
      {contextLost && (
        <p className="viewport__message" role="status">
          {messages.viewport.contextLost}
        </p>
      )}
    </section>
  );
}
